import { useState } from 'react';
import { useCharacter } from '../CharacterContext';
import { waitSeconds } from '../../../../components/helpers';
import useHandleSendXLImageRequest from '../../../../hooks/images/useHandleSendXLImageRequest';
import { biglustEngine } from '../../../../utils/engineHelpers';
import { CharacterInCreation } from '../characterTypes';

export const DEFAULT_XL_NEGATIVE_PROMPT = `score_1, score_2, (deformed body), (low quality face:1.2), (distorted face:1.2), (deformed face:1.15), (disfigured face:1.15), asymmetric face, 3d, render, cgi, illustration, painting, cartoon, disfigured, bad art, extra limbs, close up, blurry, duplicate, morbid, mutilated, out of frame, extra fingers, mutated hands, poorly drawn hands, (poorly drawn face:1.1), bad anatomy, watermark,  cropped, worst quality, jpeg artifacts, compression artifacts`;

// TODO: engine is wrong in FakeImage, maybe fix something else as well? object in image should tell in magic mode or some new? Use selected tags or new?
const createCharacterPrompt = (character: CharacterInCreation) => {
  const {
    gender,
    age,
    origin,
    name,
    faceType,
    hairColor,
    hairStyle,
    eyeColor,
    bodyType,
    boobs,
    clothing,
    place,
    composition,
    action,
  } = character;

  // Build prompt components separately to manage structure better
  const qualityDescriptors =
    'score_9, r/gonewild,  detailed, sharp focus';

  // Photography style
  const photoStyle =
    'soft lighting, high detail skin texture, detailed eyes, detailed face, detailed skin, film grain';

  // Build the core subject description with proper weighting for important elements
  let subjectDescription = '';
  if (gender) subjectDescription += `${gender}`;
  if (age) subjectDescription += `, ${age} years old`;
  if (origin) subjectDescription += `, ${origin} ethnicity`;

  // Appearance details with proper emphasis
  let appearanceDetails = '';
  if (faceType) appearanceDetails += `, (${faceType} face)`;

  if (hairColor && hairStyle) {
    appearanceDetails += `, (${hairColor} ${hairStyle} hair)`;
  } else {
    if (hairColor) appearanceDetails += `, (${hairColor} hair)`;
    if (hairStyle) appearanceDetails += `, (${hairStyle} hair)`;
  }

  if (eyeColor) appearanceDetails += `, (${eyeColor} eyes:0.7)`;
  if (bodyType) appearanceDetails += `, (${bodyType} body type)`;
  if (boobs) appearanceDetails += `, ${boobs} boobs`;

  // Context and environment details
  let contextDetails = '';
  if (clothing && clothing.length > 0) {
    // Give more weight to clothing for better detail
    contextDetails += `, (wearing ${clothing.join(', ')})`;
  }

  if (place) contextDetails += `, in ${place}`;

  // Composition and action with appropriate weighting
  let compositionDetails = '';
  if (composition) compositionDetails += `, ${composition}`;
  if (action) compositionDetails += `, (${action}:1.2)`;

  // Assemble the full prompt with a logical structure (starting with quality, then subject, etc.)
  let prompt = qualityDescriptors;

  // Add subject with proper prefixing
  prompt += `, ${photoStyle}, ${subjectDescription}${appearanceDetails}${contextDetails}${compositionDetails}`;

  if (name) {
    prompt = `${prompt}, ${name}`;
  }

  return prompt;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createCharacterNegativePrompt = (character: CharacterInCreation) => {
  return DEFAULT_XL_NEGATIVE_PROMPT;
};

const useCreateCharacter = () => {
  const [loading, setLoading] = useState(false);
  const { character, updateCharacter } = useCharacter();
  const handleSendXLImageRequest = useHandleSendXLImageRequest();

  const createCharacter = async () => {
    setLoading(true);
    await waitSeconds(1);
    const objectToGenerate = {
      fullPrompt: createCharacterPrompt(character),
      negativePrompt: createCharacterNegativePrompt(character),
      engine: biglustEngine,
      isTallPortrait: true,
    };
    const imageUrl = await handleSendXLImageRequest(true, objectToGenerate); // dirty hack, make a new hook for all this!!!
    updateCharacter({ _imageUrl: imageUrl });
    setLoading(false);
  };

  return {
    loading,
    createCharacter,
  };
};

export default useCreateCharacter;
